import React, { FC, useCallback, useMemo, useRef } from "react";
import { TiptapEditorContextState } from "@src/features/libraryItem/course/contexts/TiptapEditorContext";

export interface TiptapEditorMapState {
  getTiptapEditorState: (key: string) => TiptapEditorContextState | null;
  setTiptapEditorState: (key: string, state: TiptapEditorContextState) => void;
  getFocusedEditor: () => TiptapEditorContextState | undefined;
  subscribe: (listener: () => void) => () => void;
  lastFocusedEditorId: string | undefined;
}

export const TiptapEditorMapContext = React.createContext<TiptapEditorMapState>(
  {
    getTiptapEditorState: () => null,
    setTiptapEditorState: () => null,
    getFocusedEditor: () => undefined,
    subscribe: () => () => null,
    lastFocusedEditorId: undefined,
  },
);

type Props = {
  children: React.ReactElement;
};
const TiptapEditorMapContextProvider: FC<Props> = ({ children }) => {
  const lastFocusedEditorIdRef = useRef<string | undefined>(undefined);
  const mapRef = useRef<{
    [key: string]: TiptapEditorContextState;
  }>({});
  const listenersRef = useRef(new Set<() => void>());

  const notifyListeners = useCallback(() => {
    listenersRef.current.forEach((listener) => listener());
  }, []);

  const setTiptapEditorState = useCallback(
    (key: string, state: TiptapEditorContextState) => {
      mapRef.current[key] = state;
      if (state.editorIsFocused && lastFocusedEditorIdRef.current !== key) {
        lastFocusedEditorIdRef.current = key;
      }
      notifyListeners();
    },
    [notifyListeners],
  );
  const getTiptapEditorState = useCallback((key: string) => {
    return mapRef.current[key] || null;
  }, []);
  const getFocusedEditor: TiptapEditorMapState["getFocusedEditor"] =
    useCallback(() => {
      return Object.values(mapRef.current).find((v) => v.editorIsFocused);
    }, []);
  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);
  const contextState = useMemo<TiptapEditorMapState>(() => {
    return {
      getTiptapEditorState,
      setTiptapEditorState,
      getFocusedEditor,
      subscribe,
      get lastFocusedEditorId() {
        return lastFocusedEditorIdRef.current;
      },
    };
  }, [getTiptapEditorState, setTiptapEditorState, getFocusedEditor, subscribe]);
  return (
    <TiptapEditorMapContext.Provider value={contextState}>
      {children}
    </TiptapEditorMapContext.Provider>
  );
};
export default TiptapEditorMapContextProvider;
